import Globe from 'globe.gl';
import * as THREE from 'three';
import { SNAP_RADIUS_PX, GLOBE_AUTO_ROTATE_SPEED } from '@shared/constants.js';
import { countryCodeToFlag } from '../utils/helpers.js';
import { getCityPointRadius } from '../utils/cityGrouper.js';

/**
 * GospelGlobe — 3D globe with city-based points (Radio Garden style).
 *
 * Each point = a CITY (not a station).
 * Cities with more stations have bigger points.
 *
 * UX:
 * 1. Open → detect location → fly to nearest city → auto-play first station
 * 2. Drag the globe → releases → snaps to nearest city if close
 * 3. Crosshair detects cities using globe.getScreenCoords()
 * 4. When tuned → globe STOPS, stays locked
 * 5. Drag again → unlocks
 */

const MAGNETIC_RADIUS_PX = 40;
const SNAP_ANIM_MS = 400;

export class GospelGlobe {
  constructor({ container, crosshair, crosshairLabel, onCityTune, onCityLeave }) {
    this.container = container;
    this.crosshairEl = crosshair;
    this.crosshairLabelEl = crosshairLabel;
    this.onCityTune = onCityTune;
    this.onCityLeave = onCityLeave;

    this.cityPoints = [];
    this.activeCity = null;
    this.isTunedAndLocked = false;
    this.isUserDragging = false;
    this.isSnapping = false;
    this._dragCooldown = false;
    this._userHasInteracted = false;
    this.globe = null;

    this._init();
  }

  _init() {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;

    this._currentAltitude = 2.5; // Track zoom level for adaptive sizing

    this.globe = Globe()
      .globeImageUrl('//unpkg.com/three-globe/example/img/earth-night.jpg')
      .bumpImageUrl('//unpkg.com/three-globe/example/img/earth-topology.png')
      .backgroundImageUrl('//unpkg.com/three-globe/example/img/night-sky.png')
      .atmosphereColor('rgba(0, 229, 255, 0.2)')
      .atmosphereAltitude(0.18)
      .width(w)
      .height(h)
      .pointsData([])
      .pointLat('latitude')
      .pointLng('longitude')
      .pointAltitude(0.005)
      .pointRadius(d => this._adaptiveRadius(d.stationCount))
      .pointColor(() => '#00e5ff')
      .pointsMerge(false)
      .pointResolution(12)
      (this.container);

    this.globe.onGlobeReady(() => {
      this._setupControls();
      this._startAutoRotate();
      this._startCrosshairLoop();
      this._applyGlowMaterial();
      this._startPulseLoop();
      // Hide loading skeleton with fade-out
      document.getElementById('globe-loader')?.classList.add('hidden');
      console.log('🌍 Globe ready, crosshair loop started');
    });

    window.addEventListener('resize', () => {
      this.globe.width(this.container.clientWidth);
      this.globe.height(this.container.clientHeight);
    });
  }

  setCityPoints(cityPoints) {
    this.cityPoints = cityPoints;
    this.globe.pointsData(cityPoints);
  }

  // Keep backward compat — setStations just delegates
  setStations(cityPoints) {
    this.setCityPoints(cityPoints);
  }

  flyToCity(cityPoint, durationMs = 1500) {
    this.isSnapping = true;
    this._stopAutoRotate();

    this.globe.pointOfView({
      lat: cityPoint.latitude,
      lng: cityPoint.longitude,
      altitude: 1.8,
    }, durationMs);

    setTimeout(() => {
      this.isSnapping = false;
      this._tuneToCity(cityPoint);
    }, durationMs + 100);
  }

  // Keep backward compat
  flyToStation(cityPoint, durationMs = 1500) {
    this.flyToCity(cityPoint, durationMs);
  }

  flyToUserLocation() {
    if (!this.cityPoints.length) return;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          console.log(`📍 Browser geolocation: ${lat.toFixed(4)}, ${lng.toFixed(4)} (accuracy: ${pos.coords.accuracy}m)`);

          if (Math.abs(lat) < 1 && Math.abs(lng) < 1) {
            console.warn('[Globe] Bad coordinates (0,0), using IP fallback');
            this._flyToUserByIP();
            return;
          }
          this._flyToNearest(lat, lng);
        },
        () => {
          console.warn('[Globe] Browser geolocation denied, using IP fallback');
          this._flyToUserByIP();
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    } else {
      this._flyToUserByIP();
    }
  }

  async _flyToUserByIP() {
    try {
      const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(5000) });
      const data = await res.json();
      if (data.latitude && data.longitude) {
        console.log(`📍 IP geolocation: ${data.latitude}, ${data.longitude} (${data.city}, ${data.country_name})`);
        this._flyToNearest(data.latitude, data.longitude);
      }
    } catch (err) {
      console.warn('[Globe] IP geolocation failed:', err.message);
    }
  }

  _flyToNearest(lat, lng) {
    const nearest = this._findNearestCityByGeo(lat, lng);
    if (nearest) {
      const dist = this._haversine(lat, lng, nearest.latitude, nearest.longitude);
      console.log(`🎯 Nearest city: "${nearest.city}" (${nearest.stationCount} stations) — ${dist.toFixed(0)}km away`);
      setTimeout(() => this.flyToCity(nearest, 2500), 1000);
    }
  }

  _findNearestCityByGeo(lat, lng) {
    let nearest = null;
    let minDist = Infinity;
    for (const cp of this.cityPoints) {
      const d = this._haversine(lat, lng, cp.latitude, cp.longitude);
      if (d < minDist) { minDist = d; nearest = cp; }
    }
    return nearest;
  }

  _haversine(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // ─── Tune to a city (single source of truth) ─────────────────────

  _tuneToCity(cityPoint) {
    if (!cityPoint || cityPoint.id === this.activeCity?.id) return;

    this.activeCity = cityPoint;
    this.isTunedAndLocked = true;
    this._stopAutoRotate();
    this.crosshairEl.classList.add('active');

    const flag = countryCodeToFlag(cityPoint.country_code);
    const countLabel = cityPoint.stationCount > 1 ? ` (${cityPoint.stationCount})` : '';
    this.crosshairLabelEl.textContent = `${flag} ${cityPoint.city}${countLabel}`;

    this.onCityTune?.(cityPoint);
    this._updatePointColors();
    console.log(`📻 Tuned to city: ${cityPoint.city} (${cityPoint.stationCount} stations)`);

    // Pull city to exact center of crosshair
    this.globe.pointOfView({
      lat: cityPoint.latitude,
      lng: cityPoint.longitude,
    }, 300);
  }

  _clearTune() {
    if (!this.activeCity) return;
    this.activeCity = null;
    this.isTunedAndLocked = false;
    this.crosshairEl.classList.remove('active');
    this.crosshairLabelEl.textContent = '';
    this._updatePointColors();
    this.onCityLeave?.();
  }

  // ─── Controls ────────────────────────────────────────────────────────

  _setupControls() {
    const controls = this.globe.controls();
    if (!controls) return;

    controls.enableZoom = false;  // We handle zoom ourselves (centered)
    controls.enablePan = false;
    controls.rotateSpeed = 0.5;
    controls.enableDamping = true;
    controls.dampingFactor = 0.12;

    this._isPointerDown = false;
    const canvas = this.globe.renderer().domElement;

    canvas.addEventListener('pointerdown', (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      this._isPointerDown = true;
      this._userHasInteracted = true;

      // User starting to drag → unlock and clear
      if (this.isTunedAndLocked) {
        this._clearTune();
      }
      this.isUserDragging = true;
      this.isSnapping = false;
      this._stopAutoRotate();
      clearTimeout(this._cooldownTimer);
    });

    canvas.addEventListener('pointerup', () => {
      this._isPointerDown = false;
      this.isUserDragging = false;

      // Cooldown: ignore snaps while globe decelerates after drag
      this._dragCooldown = true;
      clearTimeout(this._cooldownTimer);
      this._cooldownTimer = setTimeout(() => {
        this._dragCooldown = false;
        // Only try snap AFTER cooldown, if globe settled near a city
        if (!this.isUserDragging && !this.isTunedAndLocked) {
          this._tryMagneticSnap();
        }
      }, 1500);
    });

    // Centered zoom (scroll wheel + pinch)
    canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      const pov = this.globe.pointOfView();
      if (!pov) return;

      const zoomSpeed = 0.12;
      const delta = e.deltaY > 0 ? zoomSpeed : -zoomSpeed;
      const newAlt = Math.max(0.3, Math.min(5.0, pov.altitude + delta));
      this._currentAltitude = newAlt;
      this.globe.pointOfView({ altitude: newAlt }, 200); // Smooth 200ms transition
      // Update point sizes for new zoom level
      this.globe.pointRadius(d => this._adaptiveRadius(d.stationCount));
    }, { passive: false });

    // Touch pinch-to-zoom for mobile
    let lastPinchDist = 0;
    canvas.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastPinchDist = Math.sqrt(dx * dx + dy * dy);
      }
    }, { passive: true });

    canvas.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (lastPinchDist > 0) {
          const pov = this.globe.pointOfView();
          if (pov) {
            const scale = lastPinchDist / dist;
            const newAlt = Math.max(0.3, Math.min(5.0, pov.altitude * scale));
            this._currentAltitude = newAlt;
            this.globe.pointOfView({ altitude: newAlt }, 0);
            this.globe.pointRadius(d => this._adaptiveRadius(d.stationCount));
          }
        }
        lastPinchDist = dist;
      }
    }, { passive: true });
  }

  _startAutoRotate() {
    const c = this.globe.controls();
    if (!c || this.isTunedAndLocked || this._userHasInteracted) return;
    c.autoRotate = true;
    c.autoRotateSpeed = GLOBE_AUTO_ROTATE_SPEED;
  }

  _stopAutoRotate() {
    const c = this.globe.controls();
    if (!c) return;
    c.autoRotate = false;
  }

  // ─── Magnetic Snap ──────────────────────────────────────────────────

  _tryMagneticSnap() {
    if (this.isUserDragging || this.isTunedAndLocked || this.isSnapping) return;

    const nearest = this._getNearestToCrosshair();
    if (nearest && nearest.distance < MAGNETIC_RADIUS_PX) {
      this.isSnapping = true;
      this._stopAutoRotate();

      this.globe.pointOfView({
        lat: nearest.cityPoint.latitude,
        lng: nearest.cityPoint.longitude,
      }, SNAP_ANIM_MS);

      setTimeout(() => {
        this.isSnapping = false;
        if (!this.isUserDragging) {
          this._tuneToCity(nearest.cityPoint);
        }
      }, SNAP_ANIM_MS + 50);
    }
    // Se não tiver cidade perto → fica parado onde está
  }

  // ─── Crosshair Detection (runs every frame) ─────────────────────────

  _startCrosshairLoop() {
    const check = () => {
      this._checkCrosshair();
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  }

  /**
   * NO DEBOUNCE — direct detection every frame.
   */
  _checkCrosshair() {
    if (!this.cityPoints.length || this.isSnapping || this._dragCooldown) return;

    // During drag: show label but don't auto-tune
    const nearest = this._getNearestToCrosshair();

    if (nearest && nearest.distance < SNAP_RADIUS_PX) {
      if (this.isUserDragging) {
        // Show label preview while dragging (no audio)
        this.crosshairEl.classList.add('active');
        const flag = countryCodeToFlag(nearest.cityPoint.country_code);
        const countLabel = nearest.cityPoint.stationCount > 1 ? ` (${nearest.cityPoint.stationCount})` : '';
        this.crosshairLabelEl.textContent = `${flag} ${nearest.cityPoint.city}${countLabel}`;
      } else if (!this.isTunedAndLocked) {
        // Not dragging, not locked → tune immediately
        this._tuneToCity(nearest.cityPoint);
      }
    } else {
      // No city near crosshair
      if (this.isUserDragging) {
        this.crosshairEl.classList.remove('active');
        this.crosshairLabelEl.textContent = '';
      } else if (!this.isTunedAndLocked && this.activeCity) {
        this._clearTune();
      }
    }
  }

  /**
   * Find nearest city point to screen center using globe.getScreenCoords()
   */
  _getNearestToCrosshair() {
    const renderer = this.globe.renderer();
    if (!renderer) return null;

    const w = renderer.domElement.clientWidth;
    const h = renderer.domElement.clientHeight;
    const centerX = w / 2;
    const centerY = h / 2;

    let nearest = null;
    let nearestDist = Infinity;

    for (const cp of this.cityPoints) {
      const coords = this.globe.getScreenCoords(cp.latitude, cp.longitude, 0.008);
      if (!coords || coords.x == null || coords.y == null) continue;

      const dx = coords.x - centerX;
      const dy = coords.y - centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = { cityPoint: cp, distance: dist };
      }
    }

    return nearest;
  }

  _updatePointColors() {
    const activeId = this.activeCity?.id;
    this.globe.pointColor(d => d.id === activeId ? '#ffffff' : '#00e5ff');
  }

  // ─── Adaptive Radius (zoom-aware) ─────────────────────────────────

  _adaptiveRadius(stationCount) {
    const base = getCityPointRadius(stationCount);
    // Scale markers based on zoom: farther = bigger, closer = smaller
    const zoomFactor = Math.max(0.6, Math.min(1.8, this._currentAltitude / 2.5));
    return base * zoomFactor;
  }

  // ─── Glow Material ────────────────────────────────────────────────

  _applyGlowMaterial() {
    // Access the Three.js scene to modify point materials
    const scene = this.globe.scene();
    if (!scene) return;

    scene.traverse((obj) => {
      if (obj.isMesh && obj.material && obj.__globeObjType === 'point') {
        obj.material.transparent = true;
        obj.material.opacity = 0.85;
      }
    });
  }

  // ─── Pulse Animation for Active City ──────────────────────────────

  _startPulseLoop() {
    let phase = 0;
    const pulse = () => {
      if (this.activeCity) {
        phase += 0.05;
        const scale = 1 + Math.sin(phase) * 0.15; // Oscillate ±15%
        const activeId = this.activeCity.id;
        this.globe.pointRadius(d => {
          const base = this._adaptiveRadius(d.stationCount);
          return d.id === activeId ? base * scale : base;
        });
      }
      requestAnimationFrame(pulse);
    };
    requestAnimationFrame(pulse);
  }
}
